import { useState } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDatabase, faLink, faClipboard, faCheck } from '@fortawesome/free-solid-svg-icons'
import { formatValue } from '../review/format'

// Called out, not just railed, when they match: these make a pair a duplicate.
const CRITICAL_KEYS = ['Invoice #', 'Amount', 'Charge date', 'Invoice date']

const ACCENT = {
  rail: 'var(--rust-500)',
  strongBg: 'var(--rust-100)',
  strongText: 'var(--rust-700)',
  faintBg: 'rgba(231,113,82,.06)'
}

// Copyable: the reviewer's next step is usually looking the record up in the ERP.
const VendorId = ({ value }) => {
  const [copied, setCopied] = useState(false)

  const copy = (event) => {
    event.stopPropagation()
    navigator.clipboard?.writeText(String(value))
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>Vendor ID:</span>
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-body)' }}>{value}</span>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Vendor ID copied' : `Copy vendor ID ${value}`}
        title="Copy to clipboard"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          lineHeight: 1,
          color: copied ? 'var(--green-600)' : 'var(--text-subtle)'
        }}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faClipboard} style={{ width: 11, height: 11 }} />
      </button>
    </span>
  )
}

VendorId.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

// Similar (match.js SIMILAR_KEYS): railed as evidence, but dashed gold so it never reads as agreement.
const RAIL_BY_STATUS = {
  match: `3px solid ${ACCENT.rail}`,
  similar: '3px dashed var(--gold-600)',
  differs: '3px solid transparent'
}

const ErpRow = ({ row, status, strong }) => {
  const match = status === 'match'
  const similar = status === 'similar'

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--border-subtle)',
        background: strong ? ACCENT.strongBg : match ? ACCENT.faintBg : similar ? 'var(--gold-100)' : 'transparent',
        borderLeft: RAIL_BY_STATUS[status]
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          flex: 'none',
          background: status === 'differs' ? 'var(--gold-500)' : 'transparent'
        }}
      />
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-muted)',
          fontWeight: 'var(--weight-semibold)',
          textTransform: 'uppercase',
          letterSpacing: '.03em',
          width: 104,
          flex: 'none'
        }}
      >
        {row.k}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-sm)',
          fontWeight: strong ? 700 : 500,
          color: strong ? ACCENT.strongText : 'var(--text-strong)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
        title={formatValue(row.v, row.kind)}
      >
        {formatValue(row.v, row.kind)}
      </span>
      {match ? (
        <FontAwesomeIcon icon={faLink} style={{ width: 12, height: 12, color: ACCENT.rail }} />
      ) : (
        <span
          style={{
            fontSize: 10,
            color: 'var(--gold-700)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.05em'
          }}
        >
          {similar ? 'similar' : 'differs'}
        </span>
      )}
    </div>
  )
}

ErpRow.propTypes = {
  row: PropTypes.object.isRequired,
  status: PropTypes.oneOf(['match', 'similar', 'differs']).isRequired,
  strong: PropTypes.bool
}

const ErpPanel = ({ side, statusMap }) => (
  <div
    style={{
      background: 'var(--surface-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden'
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '11px 14px',
        borderBottom: '1px solid var(--border-default)',
        background: 'var(--surface-card)'
      }}
    >
      <FontAwesomeIcon icon={faDatabase} style={{ width: 13, height: 13, color: 'var(--navy-600)' }} />
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 'var(--text-sm)',
          color: 'var(--text-strong)'
        }}
      >
        ERP record
      </span>
    </div>
    <div>
      {side.erp.map((row) => {
        const status = statusMap[row.k] || 'differs'
        return (
          <ErpRow
            key={row.k}
            row={row}
            status={status}
            strong={status === 'match' && CRITICAL_KEYS.includes(row.k)}
          />
        )
      })}
    </div>
  </div>
)

ErpPanel.propTypes = {
  side: PropTypes.object.isRequired,
  statusMap: PropTypes.object.isRequired
}

// Deciding the finding decides every side, so show a side's prior decision first.
const PriorDecision = ({ review }) => (
  <div
    role="note"
    style={{
      marginTop: 8,
      padding: '6px 10px',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--gold-100)',
      border: '1px solid var(--gold-200)',
      fontSize: 'var(--text-xs)',
      color: 'var(--text-body)'
    }}
  >
    <strong>Already {review.status.toLowerCase()}</strong>
    {review.reviewer && <> by {review.reviewer}</>}
    {review.reason && <> — {review.reason}</>}
  </div>
)

PriorDecision.propTypes = {
  review: PropTypes.shape({
    status: PropTypes.string.isRequired,
    reviewer: PropTypes.string,
    reason: PropTypes.string
  }).isRequired
}

const TxnColumn = ({ side, index, statusMap }) => {
  const first = index === 0
  const suspect = side.role === 'suspect'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        paddingLeft: first ? 0 : 12,
        paddingRight: 12,
        borderLeft: first ? 'none' : '1px solid var(--border-default)'
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'var(--surface-page)',
          padding: '16px 0 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}
      >
        {/* Full row: long vendor names wrapped beside the role badge. */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              fontSize: 'var(--text-lg)',
              color: 'var(--text-strong)'
            }}
          >
            {side.vendor_name || 'Unknown vendor'}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 9,
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              marginTop: 4
            }}
          >
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
                padding: '3px 8px',
                borderRadius: 'var(--radius-pill)',
                color: suspect ? 'var(--rust-700)' : 'var(--green-600)',
                background: suspect ? 'var(--rust-100)' : 'var(--green-100)'
              }}
            >
              {suspect ? 'Suspected duplicate' : 'Original'}
            </span>
            {side.external_id && <VendorId value={side.external_id} />}
          </div>
          {side.review && side.review.status !== 'Open' && <PriorDecision review={side.review} />}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 24 }}>
        <ErpPanel side={side} statusMap={statusMap} />
      </div>
    </div>
  )
}

TxnColumn.propTypes = {
  side: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  statusMap: PropTypes.object.isRequired
}

const DuplicateCompare = ({ sides, statusMap }) => {
  return (
    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 24px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${sides.length}, minmax(320px, 1fr))`
        }}
      >
        {sides.map((side, index) => (
          <TxnColumn key={side.id} side={side} index={index} statusMap={statusMap} />
        ))}
      </div>
    </div>
  )
}

DuplicateCompare.propTypes = {
  sides: PropTypes.array.isRequired,
  // key => 'match' | 'similar' | 'differs', from match.js buildStatusMap
  statusMap: PropTypes.object.isRequired
}

export default DuplicateCompare
